/**
 * @fileoverview
 * Geolocation Service - Global service for location-based operations
 * Provides geolocation with smart retry logic and error handling
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable } from '@angular/core';

export interface GeolocationResult {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

export interface GeolocationError {
  code: number;
  message: string;
}

@Injectable({
  providedIn: 'root'  // Global singleton service
})
export class GeolocationService {
  
  /**
   * Get current position with two-tier retry strategy
   * Tier 1: High accuracy GPS (30 seconds)
   * Tier 2: Fast network positioning fallback (15 seconds)
   * 
   * @returns Promise with GeolocationResult
   * @throws GeolocationError with specific error code
   */
  async getCurrentPosition(): Promise<GeolocationResult> {
    if (!this.isGeolocationSupported()) {
      throw {
        code: 0,
        message: 'Geolocation is not supported by your browser'
      } as GeolocationError;
    }

    const position = await this.getPositionWithRetry();

    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: position.timestamp,
    };
  }

  /**
   * Check if geolocation is supported
   */
  isGeolocationSupported(): boolean {
    return 'geolocation' in navigator;
  }

  /**
   * Validate if coordinates are valid geographic coordinates
   */
  isValidCoordinates(lat: number, lng: number): boolean {
    return (
      typeof lat === 'number' &&
      typeof lng === 'number' &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180 &&
      !isNaN(lat) &&
      !isNaN(lng)
    );
  }

  /**
   * Format coordinates for display
   */
  formatCoordinates(lat: number, lng: number, precision: number = 6): string {
    return `${lat.toFixed(precision)}, ${lng.toFixed(precision)}`;
  }

  /**
   * Get human-readable error message for geolocation error
   */
  getErrorMessage(error: GeolocationPositionError | GeolocationError): string {
    const code = error.code;

    switch (code) {
      case 0:
        return 'Geolocation is not supported by your browser';
      case 1: // PERMISSION_DENIED
        return 'Location Permission Denied: Please allow location access in your browser settings';
      case 2: // POSITION_UNAVAILABLE
        return 'Location Unavailable: Could not determine your location. Please try again';
      case 3: // TIMEOUT
        return 'Location Timeout: GPS signal is weak. Please move to an area with better GPS reception (near a window or outdoors) and try again.';
      default:
        return 'Could not get your location. Please enable location services and try again';
    }
  }

  /**
   * Internal method: Get position with two-tier retry strategy
   */
  private getPositionWithRetry(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      // Tier 1: High accuracy GPS (30 seconds)
      navigator.geolocation.getCurrentPosition(
        resolve,
        (error) => {
          console.log('[Geolocation] High accuracy attempt failed, trying with lower accuracy...', error);
          
          // Tier 2: Fast network positioning fallback (15 seconds)
          navigator.geolocation.getCurrentPosition(
            resolve,
            reject,
            {
              timeout: 15000,          // 15 seconds for fallback
              maximumAge: 60000,       // Accept cached position up to 1 minute old
              enableHighAccuracy: false, // Faster, less battery intensive
            }
          );
        },
        {
          timeout: 30000,             // 30 seconds for first attempt
          maximumAge: 0,              // Don't use cache on first attempt
          enableHighAccuracy: true,   // Best accuracy
        }
      );
    });
  }

  /**
   * Watch position changes (for continuous tracking)
   * Returns watchId that can be used to clear the watch
   */
  watchPosition(
    successCallback: (result: GeolocationResult) => void,
    errorCallback: (error: GeolocationError) => void,
    options?: PositionOptions
  ): number {
    if (!this.isGeolocationSupported()) {
      errorCallback({
        code: 0,
        message: 'Geolocation is not supported by your browser'
      });
      return -1;
    }

    return navigator.geolocation.watchPosition(
      (position) => {
        successCallback({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
        });
      },
      (error) => {
        errorCallback({
          code: error.code,
          message: this.getErrorMessage(error)
        });
      },
      options || {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0,
      }
    );
  }

  /**
   * Clear position watch
   */
  clearWatch(watchId: number): void {
    if (watchId >= 0) {
      navigator.geolocation.clearWatch(watchId);
    }
  }

  /**
   * Calculate distance between two coordinates (Haversine formula)
   * Note: For production, prefer using backend calculation for consistency
   * This is useful for UI display purposes only
   * 
   * @returns Distance in meters
   */
  calculateDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number {
    const R = 6371e3; // Earth radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return Math.round(R * c); // Distance in meters
  }

  /**
   * Format distance for display
   */
  formatDistance(meters: number): string {
    if (meters < 1000) {
      return `${meters}m`;
    }
    return `${(meters / 1000).toFixed(2)}km`;
  }
}
